import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  FABRIC_SOURCES,
  MAX_QUOTATION_LINES,
  paiseToRupees,
  parseRupees,
  type CreateQuotationInput,
  type FabricSource,
  type Quotation,
  type QuotationLineInput,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import type { QuotationParty } from './quotation-party-picker-dialog.js';
import { formatCurrency } from '../../lib/format.js';
import { useTemplates } from '../../lib/measurements/use-templates.js';
import {
  useCreateQuotation,
  useReviseQuotation,
  useUpdateQuotation,
} from '../../lib/quotations/use-quotations.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
} from '@tailoring-crm/ui';

/**
 * Pricing a job, before anybody has committed to it.
 *
 * The form is the booking screen's garment list with the two things a PRICE cannot know taken
 * out — no measurements, because nobody measures a bridal customer before she has agreed what it
 * costs, and no promised dates, because there is nothing yet to promise. What is left is the
 * arithmetic, and the arithmetic is deliberately the order's: stitching times quantity, plus the
 * cloth when the shop is supplying it. Half a tailoring bill is routinely the fabric, and a
 * quotation that could not carry it would be quoting half the job.
 *
 * THE TOTAL SHOWN HERE IS A PREVIEW, in exactly the sense the booking screen means it: this form
 * sends prices and never a total, the server computes the real figure with `linePaiseOf`, and if
 * the two ever disagreed the server would be right. On a registered shop the preview is also
 * pre-tax — tax is applied at send, from the settings frozen onto the document, and guessing at
 * it here would put a number on screen that the PDF then contradicts.
 *
 * ONE FORM, THREE JOBS. Drafting, correcting a draft, and revising something already sent are
 * the same fields over the same lines; what changes is the verb and what happens to the document
 * behind it. A revision in particular opens version n+1 and leaves the version being revised
 * completely alone until the new one is SENT — so a shop that starts a revision and abandons it
 * still has the quotation the customer is holding.
 */

export type QuotationFormMode = 'create' | 'edit' | 'revise';

interface DraftLine {
  /**
   * The garment being priced, or '' on a line that prices a PRODUCT instead. Exactly one of this
   * and `productId` carries a value — see `quotationLineSchema`, which explains why a quotation
   * line need not be a garment: a boutique's thread and lining are things it charges for and
   * nothing anybody measures.
   */
  garmentTypeKey: string;
  productId: string | null;
  /** The product's frozen name, shown where the garment picker would be. '' on a garment line. */
  productName: string;
  quantity: string;
  /** Rupees as typed. Converted once, on submit. */
  price: string;
  fabricSource: FabricSource;
  fabricDescription: string;
  metres: string;
  ratePerMetre: string;
  notes: string;
}

const EMPTY_LINE: DraftLine = {
  garmentTypeKey: '',
  productId: null,
  productName: '',
  quantity: '1',
  price: '',
  fabricSource: 'customer',
  fabricDescription: '',
  metres: '',
  ratePerMetre: '',
  notes: '',
};

/** A price list's paise as the rupees a price box shows, or '' for a garment priced on the piece. */
function priceInputValue(paise: number | null): string {
  return paise === null ? '' : String(paiseToRupees(paise));
}

/** Rupees as typed, in paise. Anything unparsable counts as nothing rather than blowing up. */
function paiseOf(input: string): number {
  if (input.trim() === '') return 0;
  try {
    return parseRupees(input);
  } catch {
    return 0;
  }
}

/**
 * What one line comes to — the same sum `linePaiseOf` does on the server, kept deliberately
 * short so the two can be read side by side and seen to agree.
 */
function linePaiseOf(line: DraftLine): number {
  const stitching = paiseOf(line.price) * (Number(line.quantity) || 0);
  if (line.fabricSource !== 'shop') return stitching;
  return stitching + Math.round((Number(line.metres) || 0) * paiseOf(line.ratePerMetre));
}

/** Seed the form from a document — editing a draft, or opening a revision of a sent one. */
function linesOf(quotation: Quotation): DraftLine[] {
  return quotation.lines.map((line) => ({
    garmentTypeKey: line.garmentTypeKey ?? '',
    productId: line.productId,
    productName: line.productId ? line.garmentTypeLabel : '',
    quantity: String(line.quantity),
    price: (line.unitPricePaise / 100).toString(),
    fabricSource: line.fabric?.source ?? 'customer',
    fabricDescription: line.fabric?.description ?? '',
    metres: line.fabric?.metres == null ? '' : String(line.fabric.metres),
    ratePerMetre:
      line.fabric?.ratePerMetrePaise == null
        ? ''
        : (line.fabric.ratePerMetrePaise / 100).toString(),
    notes: line.notes ?? '',
  }));
}

export interface QuotationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: QuotationFormMode;
  /** Who the price is for — a customer the shop already has. */
  party: QuotationParty;
  /** The document being corrected or revised. Absent when drafting from nothing. */
  quotation?: Quotation | null;
  /** Whether this shop charges tax, so the form can say what the preview leaves out. */
  registered: boolean;
  onSaved?: (quotation: Quotation) => void;
}

export function QuotationFormDialog({
  open,
  onOpenChange,
  mode,
  party,
  quotation = null,
  registered,
  onSaved,
}: QuotationFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const templates = useTemplates();

  const create = useCreateQuotation();
  const update = useUpdateQuotation();
  const revise = useReviseQuotation();

  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  const [discount, setDiscount] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Seeded when the dialog OPENS, not on every render of the parent.
   *
   * A revision starts as a copy of what was quoted before, because the customer negotiated one
   * number and the rest of the job is unchanged — retyping five lines to move one price is how a
   * revision comes to differ from the original in ways nobody intended.
   */
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (quotation) {
      setLines(linesOf(quotation));
      setDiscount(quotation.discountPaise > 0 ? (quotation.discountPaise / 100).toString() : '');
      setValidUntil(quotation.validUntil ? quotation.validUntil.slice(0, 10) : '');
      setNotes(quotation.notes ?? '');
      return;
    }
    setLines([{ ...EMPTY_LINE }]);
    setDiscount('');
    setValidUntil('');
    setNotes('');
  }, [open, quotation]);

  const subtotalPaise = useMemo(
    () => lines.reduce((sum, line) => sum + linePaiseOf(line), 0),
    [lines],
  );
  const discountPaise = Math.min(paiseOf(discount), subtotalPaise);
  const totalPaise = subtotalPaise - discountPaise;

  const busy = create.isPending || update.isPending || revise.isPending;

  const setLine = (index: number, patch: Partial<DraftLine>): void => {
    setLines((previous) =>
      previous.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  };

  /**
   * Picking a garment brings its price-list price into the price box — the same rule as the
   * booking screen's `chooseGarment`. A price that still reads exactly as the PREVIOUS garment's
   * was put there by that garment and is replaced; anything else was typed by a person and is left
   * alone. Clearing the garment clears an auto-filled price with it.
   */
  const chooseGarment = (index: number, garmentTypeKey: string): void => {
    const all = templates.data?.templates ?? [];
    setLines((previous) =>
      previous.map((line, position) => {
        if (position !== index) return line;
        const chosen = all.find((template) => template.garmentTypeKey === garmentTypeKey);
        const before = all.find((template) => template.garmentTypeKey === line.garmentTypeKey);
        const typedByHand =
          line.price.trim() !== '' &&
          line.price !== priceInputValue(before?.unitPricePaise ?? null);
        return {
          ...line,
          garmentTypeKey,
          ...(typedByHand ? {} : { price: priceInputValue(chosen?.unitPricePaise ?? null) }),
        };
      }),
    );
  };

  const submit = async (): Promise<void> => {
    setError(null);
    // A product line is usable without a garment; a garment line is not usable without one. This
    // is the filter that silently drops half-filled rows, which is why the garment select on a
    // seeded line is rendered as REQUIRED-looking rather than left to be quietly discarded.
    const usable = lines.filter((line) => line.garmentTypeKey !== '' || line.productId !== null);
    if (usable.length === 0) {
      setError(t('quotations.form.needLine'));
      return;
    }

    const payload: QuotationLineInput[] = usable.map((line) => ({
      ...(line.productId ? { productId: line.productId } : { garmentTypeKey: line.garmentTypeKey }),
      quantity: Number(line.quantity) || 1,
      unitPricePaise: paiseOf(line.price),
      // Sent only when the shop is supplying the cloth. A `customer` line carries no metres and
      // no rate, because there is nothing to charge for and a stray rate on it would be billed.
      ...(line.fabricSource === 'shop'
        ? {
            fabric: {
              source: 'shop' as const,
              description: line.fabricDescription.trim() || null,
              metres: line.metres.trim() === '' ? null : Number(line.metres),
              ratePerMetrePaise: paiseOf(line.ratePerMetre),
            },
          }
        : {}),
      notes: line.notes.trim() || null,
    }));

    const common = {
      lines: payload,
      discountPaise,
      // An explicit null means the price does not lapse; omitting it would take the service's
      // default of fifteen days instead, which is a different promise.
      validUntil: validUntil || null,
      notes: notes.trim() || null,
    };

    try {
      const saved =
        mode === 'create'
          ? await create.mutateAsync({
              // Whose price this is, settled before this dialog opens — by the picker or by the
              // customer hub.
              customerId: party.id,
              ...common,
            } satisfies CreateQuotationInput)
          : mode === 'edit'
            ? await update.mutateAsync({ quotationId: quotation?.id ?? '', patch: common })
            : await revise.mutateAsync({ quotationId: quotation?.id ?? '', input: common });
      onSaved?.(saved);
      onOpenChange(false);
    } catch (caught) {
      setError(
        isApiRequestError(caught) || caught instanceof Error
          ? caught.message
          : t('error.unexpected'),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="atelier-dialog sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'revise'
              ? t('quotations.form.reviseTitle', { number: quotation?.number ?? '' })
              : mode === 'edit'
                ? t('quotations.form.editTitle')
                : t('quotations.form.createTitle', { name: party.name })}
          </DialogTitle>
          <DialogDescription>
            {mode === 'revise' ? t('quotations.form.reviseHint') : t('quotations.form.hint')}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          {lines.map((line, index) => (
            <div key={index} className="flex flex-col gap-3 rounded-lg border border-border p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {/*
                  A PRODUCT line has nothing to pick: it is already a named thing off the price
                  list, and offering a garment select beside it would invite somebody to file
                  thread as a blouse. Its frozen name is shown instead, in the same slot, so the
                  rows stay aligned and every line still says what it is.
                */}
                {line.productId ? (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`quotation-product-${index}`}>
                      {t('quotations.form.productLine')}
                    </Label>
                    <p
                      id={`quotation-product-${index}`}
                      className="flex h-11 items-center rounded-md border border-input bg-muted px-3 text-base text-muted-foreground sm:text-sm"
                    >
                      {line.productName}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`quotation-garment-${index}`}>
                      {t('quotations.form.garmentType')}
                    </Label>
                    <select
                      id={`quotation-garment-${index}`}
                      value={line.garmentTypeKey}
                      onChange={(event) => chooseGarment(index, event.target.value)}
                      className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                    >
                      <option value="" />
                      {(templates.data?.templates ?? []).map((template) => (
                        <option key={template.garmentTypeKey} value={template.garmentTypeKey}>
                          {template.garmentTypeLabel}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`quotation-quantity-${index}`}>
                    {t('quotations.form.quantity')}
                  </Label>
                  <Input
                    id={`quotation-quantity-${index}`}
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(event) => setLine(index, { quantity: event.target.value })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`quotation-price-${index}`}>{t('quotations.form.price')}</Label>
                  <Input
                    id={`quotation-price-${index}`}
                    inputMode="decimal"
                    value={line.price}
                    onChange={(event) => setLine(index, { price: event.target.value })}
                  />
                </div>
              </div>

              {/* Who supplies the cloth. It changes the price, so it is asked on every line
                  rather than assumed once for the whole quotation. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`quotation-fabric-${index}`}>{t('quotations.form.fabric')}</Label>
                  <select
                    id={`quotation-fabric-${index}`}
                    value={line.fabricSource}
                    onChange={(event) =>
                      setLine(index, { fabricSource: event.target.value as FabricSource })
                    }
                    className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                  >
                    {FABRIC_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {t(`quotations.form.fabricSource.${source}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`quotation-line-notes-${index}`}>
                    {t('quotations.form.lineNotes')}
                  </Label>
                  <Input
                    id={`quotation-line-notes-${index}`}
                    value={line.notes}
                    onChange={(event) => setLine(index, { notes: event.target.value })}
                  />
                </div>
              </div>

              {/* Only when the shop supplies it — there is nothing to charge for otherwise, and
                  three empty boxes on every line is a form nobody fills in. */}
              {line.fabricSource === 'shop' ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`quotation-cloth-${index}`}>
                      {t('quotations.form.fabricDescription')}
                    </Label>
                    <Input
                      id={`quotation-cloth-${index}`}
                      value={line.fabricDescription}
                      onChange={(event) =>
                        setLine(index, { fabricDescription: event.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`quotation-metres-${index}`}>
                      {t('quotations.form.metres')}
                    </Label>
                    <Input
                      id={`quotation-metres-${index}`}
                      inputMode="decimal"
                      value={line.metres}
                      onChange={(event) => setLine(index, { metres: event.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`quotation-rate-${index}`}>
                      {t('quotations.form.ratePerMetre')}
                    </Label>
                    <Input
                      id={`quotation-rate-${index}`}
                      inputMode="decimal"
                      value={line.ratePerMetre}
                      onChange={(event) => setLine(index, { ratePerMetre: event.target.value })}
                    />
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('quotations.form.lineTotal', {
                    amount: formatCurrency(linePaiseOf(line)),
                  })}
                </span>
                {lines.length > 1 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('quotations.form.removeLine', { position: index + 1 })}
                    onClick={() =>
                      setLines((previous) => previous.filter((_, position) => position !== index))
                    }
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            </div>
          ))}

          {lines.length < MAX_QUOTATION_LINES ? (
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setLines((previous) => [...previous, { ...EMPTY_LINE }])}
            >
              <Plus aria-hidden="true" />
              {t('quotations.form.addLine')}
            </Button>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-discount">{t('quotations.form.discount')}</Label>
            <Input
              id="quotation-discount"
              inputMode="decimal"
              value={discount}
              onChange={(event) => setDiscount(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-valid-until">{t('quotations.form.validUntil')}</Label>
            <Input
              id="quotation-valid-until"
              type="date"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('quotations.form.validUntilHint')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quotation-notes">{t('quotations.form.notes')}</Label>
          <Textarea
            id="quotation-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t('quotations.form.notesPlaceholder')}
          />
        </div>

        <dl className="flex flex-col gap-1 border-t border-border pt-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t('quotations.form.subtotal')}</dt>
            <dd className="tabular-nums">{formatCurrency(subtotalPaise)}</dd>
          </div>
          {discountPaise > 0 ? (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('quotations.form.discount')}</dt>
              <dd className="tabular-nums">−{formatCurrency(discountPaise)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between text-base font-semibold">
            <dt>{t('quotations.form.total')}</dt>
            <dd className="tabular-nums">{formatCurrency(totalPaise)}</dd>
          </div>
        </dl>
        {/* Said plainly rather than guessed at: the tax is applied at send, from the settings
            frozen onto the document, and a figure invented here would be one the PDF disagrees
            with in front of a customer. */}
        <p className="text-xs text-muted-foreground">
          {registered ? t('quotations.form.taxHint') : t('quotations.form.previewHint')}
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {mode === 'revise' ? t('quotations.form.saveRevision') : t('quotations.form.saveDraft')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuotationFormDialog;
